import type { ISelectCreatableGroup, ISelectCreatableOption } from "./select-creatable.types";
import { matchesGroupedSearch } from "../select.utilities";

/**
 * Find the option whose label equals some text, ignoring case.
 * @param options The options to look through.
 * @param text The text to compare the labels with.
 * @returns The matching option, if any.
 */
export const findOptionByLabel = (options: ISelectCreatableOption[], text: string) => {
    const normalisedText = text.toLowerCase();
    return options.find(({ label }) => label.toLowerCase() === normalisedText);
};

/**
 * Keep the options whose label, or group name, contains a search, ignoring case. Matching on the group name too means
 * searching a group finds all of its options.
 * @param options The options to filter.
 * @param search The text to look for.
 * @returns The matching options, in their original order.
 */
export const filterOptions = (options: ISelectCreatableOption[], search: string) =>
    options.filter(({ label, group }) => matchesGroupedSearch(label, group, search));

/**
 * Keep the options that can be shown: all of them in a flat list, or only those in one of the groups when grouped.
 * @param options The options to look through.
 * @param groups The group names, when the options are grouped.
 * @returns The options that can be shown, in their original order.
 */
export const getAvailableOptions = (options: ISelectCreatableOption[], groups: string[] | undefined) =>
    typeof groups === "undefined"
        ? options
        : options.filter(({ group }) => typeof group !== "undefined" && groups.includes(group));

/**
 * Split options into their groups, in the groups' order, leaving out groups with no options.
 * @param options The options to split.
 * @param groups The group names, in display order.
 * @returns Each non-empty group with its options.
 */
export const groupOptions = (options: ISelectCreatableOption[], groups: string[]): ISelectCreatableGroup[] =>
    groups
        .map((name) => ({ name, options: options.filter(({ group }) => group === name) }))
        .filter((group) => group.options.length > 0);
